import {BaseElementAsync, MsgLabel, OSkinableInit} from "/@back@/commons/basis.js";
import {CellBuilderString} from "/@back@/commons/widgets/grid-libs.js";
import {Grid, OGridSmallInit} from "/@back@/commons/widgets/grid-tags.js";
import {GridColTreeDef, GridDataHolderJsonTree, GridDataRowJsonTree} from "/@back@/commons/widgets/tree.js";
import {IReg, REG} from "/@lib@/commons/registry.js";
import {DOM, JSX} from "/@lib@/commons/xml/dom.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {EUserAspects, EUserDisabledFilters, EUserType, JUser, USER, UsersSrv} from "/@lib@/core/user.js";
import {IAction} from "/@lib@/commons/actions.js";
import {LANG} from "/@lib@/commons/lang.js";
import {IContextMenuActionsPointer} from "/@back@/commons/actionables.js";
import {Button, ButtonToggle, OButtonInit, OButtonToggleInit} from "/@back@/commons/widgets/buttons.js";
import {InputUserPanel, OInputUserInit} from "/@back@/core/widgets/inputs.js";
import {OUserRefInit, UserRef} from "/@back@/core/widgets/userRef.js";
import {ICoreUniverseEnv} from "/@lib@/core/universe.js";
import {IRoleEditUiHandlerContext, RoleEditUiHandler, ROLES} from "/@lib@/commons/roles.js";
import {RoleUiHandlerContextUsed} from "/@back@/core/dialogs/rolesExplorer.js";
import {IRowSortFn} from "/@back@/commons/widgets/grid-core.js";

/**
 * Widget d'affichage d'une liste de users avec filtre.
 */
export interface UsersGrid<E> extends BaseElementAsync {
	initialize(init?: OUsersGridInit<E>): this
}

export interface OUsersGridInit<E> extends OSkinableInit {
	reg?: IReg<ICoreUniverseEnv>

	usersSrv?: UsersSrv;

	/** Modification de la liste des users obtenue : ajout d'éléments (user anonymous si non présent dans la liste), ... */
	normalizeList?: (array: JUser[], filterTxtRegExp?: RegExp) => JUser[],

	/** Affichage d'un btn de raz des filtres */
	displayRazFilterBtn?: boolean,

	/** Filtre libre appliqué sur les entrées affichées
	 * 		fonction : retourne true si l'entrée est préservée */
	openFilter?: (entry: JUser) => boolean,

	/** Filtre TXT : affichage du champ input "txt" - true par défaut */
	filterTxtInputVisibility?: boolean

	/** Filtre TXT : valeur de recherche initiale */
	filterTxt?: string

	/** Filtre TYPE : affichage du champ input "type" - false par défaut
	 * 		Note : affiché SSi le usersSrv possède l'aspect EUserAspects.groupable */
	filterTypeInputVisibility?: boolean

	/** Filtre TYPE : valeur de recherche initiale - null par défaut (ie tous les types) */
	filterType?: EUserType

	/** Filtre HIDDEN : affichage du champ input "hidden" - false par défaut
	 * 		Note : affiché SSi le usersSrv possède l'aspect EUserAspects.hideable,
	 * 	et s l'utilisateur courant possède la permission {@link OUsersGridInit.filterHiddenInputPerms}  */
	filterHiddenInputVisibility?: boolean

	/** Filtre HIDDEN : perms UI requises pour afficher ce bouton */
	filterHiddenInputPerms?: string[]

	/** Filtre TYPE : valeur de recherche initiale */
	filterHidden?: boolean | null

	/** Filtre TYPE : initialisation du bounton */
	filterHiddenBtnInit?: OButtonToggleInit

	/** Filtre GROUPS : affichage du champ input "groups"  - false par défaut
	 * 		Note : affiché SSi le usersSrv possède l'aspect EUserAspects.groupable ET au moins un groupe a été défini */
	filterGroupsInputVisibility?: boolean

	filterGroupsInputInit?: OInputUserInit<E>

	/** Filtre GROUPS : valeur de recherche initiale ou fixées */
	filterGroups?: string[] | string

	/**
	 * filterDisabledAccounts
	 * 	false par défaut
	 */
	filterDisabledInputVisibility?: boolean

	/** @see eu.scenari.commons.user.IUsersBrowser.EUserDisabledFilters */
	filterDisabled?: null | 'disabledPermanent' | 'NotDisabledPermanent'

	/** Filtre ROLES : affichage du champ input "roles" - false par défaut
	 * 		Note : affiché SSi le usersSrv possède l'aspect EUserAspects.rolable */
	filterRolesInputVisibility?: boolean

	/** Filtre ROLES : valeur de recherche initiale ou fixées */
	filterRoles?: string //evol : string[]

	/** Préserve la sélection courante lors d'un changement de filtre. False par défaut */
	preserveSelectionOnFilter?: boolean

	/** Permet de modifier la valeur initiale du nombre maximal d'utilisateurs affichés */
	firstMaxEntries?: number

	/** Permet de modifier la valeur du nombre maximal d'utilisateurs affichés en mode 'expand'
	 * Si fullMaxEntries est inférieur à firstMaxEntries, alors ce concept d'affichage étendu n'est pas proposé */
	fullMaxEntries?: number

	/** Force la sélection d'une entrée (la première si rien n'est spécifié) **/
	forceSelectEntry?: false

	/** Paramétrage du cellBuilder : ajoute le longname derrière le nick or account */
	showLongName?: boolean

	/** Si true, l'appel de doSearchUsers est assumé par le contexte exploitant ce widget **/
	disableAutoSearchOnInit?: boolean

	/** Action excutée au double-click ou sur les frappes entrée ou espace. */
	defaultAction?: IAction<E>

	grid?: OGridSmallInit
}

/**
 *
 */

export class UsersGrid<E> extends BaseElementAsync {

	params: OUsersGridInit<E>;

	reg: IReg<ICoreUniverseEnv>;

	usersSrv: UsersSrv;

	dataHolder: GridDataHolderJsonTree<JUser>;

	grid: Grid & IContextMenuActionsPointer<E>

	/** Formulaire de filtre */
	filterFormElt: HTMLFormElement;

	/** Formulaire de filtre : filtre par groupe */
	filterFormGroupsInput: InputUserPanel<E> | null;

	/** Formulaire de filtre : filtre par role */
	filterFormRolesInput: HTMLSelectElement | null;

	msgElt: MsgLabel;

	razButton: Button;

	searchMoreBtn: Button;

	protected async _initialize(init: OUsersGridInit<E>): Promise<void> {
		this.reg = this.findReg(init);
		this.params = init;

		// Valeur par défaut des params
		if (this.params.firstMaxEntries === undefined) this.params.firstMaxEntries = 100;
		if (this.params.fullMaxEntries === undefined) this.params.fullMaxEntries = 1000;
		if (this.params.filterTxtInputVisibility === undefined) this.params.filterTxtInputVisibility = true;
		if (this.params.filterTypeInputVisibility === undefined) this.params.filterTypeInputVisibility = false;
		if (this.params.filterDisabledInputVisibility === undefined) this.params.filterDisabledInputVisibility = false;
		if (this.params.filterHiddenInputVisibility === undefined) this.params.filterHiddenInputVisibility = false;
		if (this.params.filterHidden === undefined) this.params.filterHidden = false;
		if (this.params.preserveSelectionOnFilter === undefined) this.params.preserveSelectionOnFilter = false;

		this.usersSrv = init.usersSrv || this.reg.env.universe.useUsers;

		this.dataHolder = new GridDataHolderJsonTree<JUser>('children');

		const colDefs = [new GridColTreeDef('tree')
			.setFlex('1rem', 1, 1).setMinWidth('55px')
			.setCellBuilder(new CellBuilderAccountJUser(this.reg, false, this.params.showLongName))];

		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this.reg.installSkin("form-control-areas", sr);
		this._initAndInstallSkin(this.localName, init);

		// Filtres
		let headElt = sr.appendChild(<div id="head"/>) as HTMLDivElement;
		this.filterFormElt = headElt.appendChild(<form id="filter" title="Filtres appliqués à la liste des utilisateurs..."/>) as HTMLFormElement;
		this.filterFormElt.onsubmit = (ev) => false;

		if (this.params.filterTxtInputVisibility) {
			let input = this.filterFormElt.appendChild(<input type="search" id="filterTxt" name="filterTxt" spellcheck="false" autocomplete="off" oninput={this.onSearchInput} onkeydown={this.onKeyPress}/>) as HTMLInputElement;
			input.focus();
		}
		if (this.params.filterTypeInputVisibility && this.usersSrv.hasAspect(EUserAspects.groupable)) {
			let input = this.filterFormElt.appendChild(<select id="filterType" name="filterType" onchange={this.onSearchInput}>
				<option value="" title="Utilisateurs et groupes" label="Utilisateurs et groupes"/>
				<option value="user" title="Utilisateurs" label="Utilisateurs"/>
				<option value="group" title="Groupes" label="Groupes"/>
			</select>) as HTMLSelectElement;
			input.addEventListener("change", function (ev) {DOM.setAttr(this, "data-value", this.value)});
		}
		if (this.params.filterHiddenInputVisibility && this.usersSrv.hasAspect(EUserAspects.hideable) && (!this.params.filterHiddenInputPerms || this.reg.hasPerm(this.params.filterHiddenInputPerms))) {
			this.filterFormElt.appendChild(<ButtonToggle id="filterHidden" onclick={this.toggleFilterHiddenInput} î={Object.assign({
				toggleOn: this.params.filterHidden,
				uiContext: "bar",
				icon: "/@skin@/core/objects/user/system.svg",
				title: "Afficher/cacher les utilisateurs masqués"
			} as OButtonToggleInit, this.params.filterHiddenBtnInit)}/>) as ButtonToggle;
		}
		if (this.params.filterDisabledInputVisibility) {
			let input = this.filterFormElt.appendChild(<select id="filterDisabled" name="filterDisabled" onchange={this.onSearchInput}>
				<option value="" title="Non filtré" label="Comptes actifs et inactifs (non filtré)"/>
				<option value="disabledPermanent" title="Inactifs uniquement" label="N'afficher que les comptes inactifs"/>
				<option value="NotDisabledPermanent" title="Actifs uniquement" label="N'afficher que les comptes actifs (et les comptes désactivés temporairement)"/>
			</select>) as HTMLSelectElement;
			input.addEventListener("change", function (ev) {DOM.setAttr(this, "data-value", this.value)});
			input.addEventListener("change", function (ev) {
				this.title = "Comptes actifs et inactifs (non filtré)";
				switch (this.value as EUserDisabledFilters) {
				case "disabledPermanent":
					this.title = "N'afficher que les comptes inactifs";
					break;
				case "NotDisabledPermanent":
					this.title = "N'afficher que les comptes actifs (et les comptes désactivés temporairement)";
					break;
				default :
					this.title = "Comptes actifs et inactifs (non filtré)";
				}
			});
		}

		if (this.params.filterGroupsInputVisibility && this.usersSrv.hasAspect(EUserAspects.groupable)) {
			this.filterFormGroupsInput = this.filterFormElt.appendChild(new InputUserPanel().initialize(Object.assign({
				name: "filterGroups",
				reg: this.reg,
				emptySelectionMsg: "Membres de l'un des groupes...",
				userCard: "multi",
				usersGridInit: {
					usersSrv: this.usersSrv,
					filterType: EUserType.group,
					filterTypeInputVisibility: false,
				},
			} as OInputUserInit<E>, this.params.filterGroupsInputInit))) as InputUserPanel<E>;
			await this.filterFormGroupsInput.initializedAsync;
			this.filterFormGroupsInput.addEventListener("change", async (evt) => {
				this.onSearchInput.call(this.filterFormGroupsInput, evt);
			});
			this.filterFormGroupsInput.title = "Membres de l'un des groupes spécifiés";
			DOM.setHidden(this.filterFormGroupsInput, true);
		}

		if (this.params.filterRolesInputVisibility && this.usersSrv.hasAspect(EUserAspects.rolable)) {
			let rolesUiHandlers = await Promise.all(this.reg.mergeLists(ROLES.UI_ROLES_LIST)) as (RoleEditUiHandler<IRoleEditUiHandlerContext> & RoleUiHandlerContextUsed)[];

			this.filterFormRolesInput = this.filterFormElt.appendChild(
				<select id="filterRoles" name="filterRoles" onchange={this.onSearchInput} title="Possèdent le rôle sélectionné">
					<option value="" label="Possèdent le rôle..." disabled selected style="display: none;"/>
					<option value="" label=""/>
				</select>
			) as HTMLSelectElement;
			rolesUiHandlers.forEach((entry => {
				this.filterFormRolesInput.appendChild(<option value={entry.roleCode} title={entry.getDescription(this)} label={entry.getLabel(this)}/>)
			}));
			DOM.setHidden(this.filterFormRolesInput, true);
			// isNotSet : pour gestion du placeholder nativement de "select"...
			this.filterFormRolesInput.addEventListener("change", async (evt) => {
				DOM.setAttrBool(this.filterFormRolesInput, "isNotSet", this.filterFormRolesInput.value === '');
				if (this.filterFormRolesInput.value === "") this.filterFormRolesInput.value = "";
			});
		}

		if (this.params.displayRazFilterBtn) this.razButton = this.filterFormElt.appendChild(
			<Button id="btnRazFilter"
							î={{
								uiContext: "bar",
								title: "Réinitialiser les filtres...",
								icon: "/@skin@/commons/icons/reset-filter.svg",
							} as OButtonInit}
							onclick={() => {if (!this.razButton.disabled) this.razFilters(true)}}/> as Button);

		await this.razFilters();

		const msgBox = headElt.appendChild(<div id="msgBox"/>);

		this.msgElt = msgBox.appendChild(<MsgLabel id="msg"/>) as MsgLabel;

		if (this.params.fullMaxEntries > this.params.firstMaxEntries) this.searchMoreBtn = msgBox.appendChild(<Button id="btnMore" î={{uiContext: "dialog", label: "Voir plus..."} as OButtonInit}
																																																									onclick={this.doSearchUsers.bind(this, this.params.fullMaxEntries)}/> as Button);


		this.grid = sr.appendChild(new Grid().initialize(Object.assign({
			autoSelOnFocus: "first",
			columnDefs: colDefs,
			dataHolder: this.dataHolder,
			hideHeaders: true,
			defaultAction: init.defaultAction,
			defaultActionCtx: this,
			emptyBody: () => <span>Aucune entrée</span>
		}, init.grid)));
		if (!init.disableAutoSearchOnInit) await this.doSearchUsers();
	}

	/** Réinitialisation des différentes zones de filtre */
	public async razFilters(refreshGrid = false): Promise<void> {
		const sr = this.shadowRoot;
		// - filterTxt
		const filterTxt = sr.getElementById("filterTxt") as HTMLInputElement;
		if (filterTxt)
			filterTxt.value = this.params.filterTxt || "";
		// - filterType
		const filterType = sr.getElementById("filterType") as HTMLSelectElement;
		if (filterType) {
			if (this.params.filterType) {
				DOM.setAttr(filterType, "data-value", this.params.filterType);
				filterType.value = this.params.filterType;
			} else {
				filterType.removeAttribute("data-value");
				filterType.value = undefined;
			}
		}
		// - filterHidden
		const filterHidden = sr.getElementById("filterHidden") as ButtonToggle;
		if (filterHidden)
			filterHidden.toggleOn = this.params.filterHidden;
		// -filterDisabled
		const filterDisabled = sr.getElementById("filterDisabled") as HTMLSelectElement;
		if (filterDisabled) {
			filterDisabled.title = "Comptes actifs et inactifs (non filtré)";
			if (this.params.filterDisabled) {
				DOM.setAttr(filterDisabled, "data-value", this.params.filterDisabled);
				switch (this.params.filterDisabled) {
				case "disabledPermanent":
					filterDisabled.title = "N'afficher que les comptes inactifs";
					break;
				case "NotDisabledPermanent":
					filterDisabled.title = "N'afficher que les comptes actifs (et les comptes désactivés temporairement)";
					break;
				}
				filterDisabled.value = this.params.filterDisabled;
			} else {
				filterDisabled.removeAttribute("data-value");
				filterDisabled.value = undefined;
			}
		}
		// - filterFormGroupsInput
		if (this.filterFormGroupsInput) {
			if (this.usersSrv.hasAspect(EUserAspects.groupable)) {
				// raz
				if (typeof this.params.filterGroups === "string") this.params.filterGroups = [this.params.filterGroups];
				this.filterFormGroupsInput.value = this.params.filterGroups || null;
				const groupsSet = await this.usersSrv.list(null, EUserType.group, false, null, 1);
				DOM.setHidden(this.filterFormGroupsInput, !groupsSet.userList || groupsSet.userList.length == 0);
			} else
				DOM.setHidden(this.filterFormGroupsInput, true);
		}
		// - filterFormRolesInput
		if (this.filterFormRolesInput) {
			if (this.usersSrv.hasAspect(EUserAspects.rolable)) {
				// raz
				this.filterFormRolesInput.value = this.params.filterRoles || "";
				DOM.setAttrBool(this.filterFormRolesInput, "isNotSet", this.params.filterRoles ? false : true);
				DOM.setHidden(this.filterFormRolesInput, false);
			} else
				DOM.setHidden(this.filterFormRolesInput, true);
		}
		if (refreshGrid)
			await this.doSearchUsers();
	}

	/** Filtre des utilisateurs
	 * Retourne true si l'entrée est préservée
	 */
	setContextualUserFilter(filter: (entry: JUser) => boolean) {
		this._contextualUserFilter = filter;
	}

	_contextualUserFilter: (entry: JUser) => boolean;

	getSelectedUser(): JUser | null {
		if (this.dataHolder) {
			const row = this.dataHolder.getRow(this.grid.getSelectedRow());
			if (!row) return null;
			return row.rowDatas;
		}
	}

	getSelectedUsers(): JUser[] {
		return (this.dataHolder && this.dataHolder.countRows() > 0) ? this.dataHolder.getSelectedDatas() as JUser[] : [];
	}

	/**
	 * Sélection des entrées de l'arbre via une liste de JUser sur un tree **chargé**
	 * Si un itemType demandé n'est pas présent dans le tree, aucune erreur n'est remontée
	 * @param JUser[]
	 */
	selectByJUser(users: JUser[], showUserIfUnknown: boolean = false): void {
		if (users != null) {
			// Injection des users inconnus dans le tree
			if (showUserIfUnknown) {
				const usersSet = this.dataHolder.getDatas();
				users.forEach((entry) => {
						if (!usersSet.find((user) => user.account == entry.account)) {
							this.dataHolder.insertRowKey(null, entry);
						}
					}
				)
			}
			const selectedRows: number[] = [];
			for (let i = 0, c = this.dataHolder.countRows(); i < c; i++) {
				const userRow = this.dataHolder.getRow(i).rowDatas as JUser;
				if (userRow && users.find(user => {
					if (user && user.account == userRow.account)
						return true;
				})) {
					selectedRows.push(i);
				}
			}

			if (selectedRows.length > 0)
				this.grid.setSelectedRows(selectedRows);
			else
				this.grid.clearSel();
		}
	}

	private toggleFilterHiddenInput(this: ButtonToggle, ev: InputEvent) {
		const tree = DOMSH.findHost(this) as UsersGrid<E>;
		this.toggleOn = !this.toggleOn;
		tree.doSearchUsers();
	}


	private _searchInputWaiter: number;

	private onSearchInput(this: HTMLInputElement, ev: InputEvent) {
		const me = DOMSH.findHost(this) as UsersGrid<E>;
		if (me._searchInputWaiter) clearTimeout(me._searchInputWaiter);
		me._searchInputWaiter = setTimeout(() => me.doSearchUsers(), 500) as any;
	}

	/**
	 * Raffraichit la liste des users
	 * @param max
	 * <ul>
	 * 	<li>integer : borne max explicitement définie</li>
	 * 	<li>null : this._currentMaxEntries ou firstMaxEntries à défaut</li>
	 * 	<li>undefined : raz de 'this._currentMaxEntries', firstMaxEntries</li>
	 * </ul>
	 */
	async doSearchUsers(max?: number | null | undefined) {
		let filterIsDirty = false;
		if (max === undefined || max)
			this._currentMaxEntries = max
		let fd = new FormData(this.filterFormElt);
		const selectedUsers: JUser[] = this.getSelectedUsers();
		this.dataHolder.setDatas([]);

		let filterTxtRegExp = null;
		if (fd.has("filterTxt")) {
			if (fd.get("filterTxt")) {
				filterTxtRegExp = new RegExp(".*" + fd.get("filterTxt").toString().replace(/\W/g, LANG.escapeChar4Regexp) + ".*", "i");
				filterIsDirty = true;
			}
		} else if (this.params.filterTxt)
			filterTxtRegExp = new RegExp(".*" + this.params.filterTxt.replace(/\W/g, LANG.escapeChar4Regexp) + ".*", "i");

		let filterType: EUserType = null;
		if (this.usersSrv.hasAspect(EUserAspects.groupable)) {
			if (fd.has("filterType")) {
				filterIsDirty = true;
				filterType = fd.get("filterType") as EUserType;
			} else if (this.params.filterType)
				filterType = this.params.filterType;
		}

		let filterDisabled: EUserDisabledFilters = null;
		if (this.usersSrv.hasAspect(EUserAspects.groupable)) {
			if (fd.has("filterDisabled")) {
				filterIsDirty = true;
				filterDisabled = fd.get("filterDisabled") as EUserDisabledFilters;
			} else if (this.params.filterDisabled)
				filterDisabled = this.params.filterDisabled as EUserDisabledFilters;
		}

		let filterGroups: string[] | undefined = undefined;
		if (this.usersSrv.hasAspect(EUserAspects.groupable)) {
			if (this.filterFormGroupsInput) {
				filterGroups = this.filterFormGroupsInput.value;
				if (this.filterFormGroupsInput.value?.length) filterIsDirty = true;
			} else if (this.params.filterGroups) {
				if (typeof this.params.filterGroups === "string") this.params.filterGroups = [this.params.filterGroups];
				filterGroups = this.params.filterGroups;
			}
		}

		let filterRoles: string[] | undefined = undefined;
		if (this.usersSrv.hasAspect(EUserAspects.rolable)) {
			if (this.filterFormRolesInput?.value) {
				filterIsDirty = true;
				filterRoles = [this.filterFormRolesInput.value];
			} else {
				filterRoles = this.params.filterRoles ? [this.params.filterRoles] : null;
			}
		}

		let filterHidden: boolean | undefined = undefined;
		if (this.usersSrv.hasAspect(EUserAspects.hideable)) {
			let btnInput = this.filterFormElt.querySelector("#filterHidden") as ButtonToggle;
			if (btnInput) {
				filterHidden = btnInput.toggleOn;
				if (filterHidden != this.params.filterHidden) filterIsDirty = true;
			} else if (this.params.filterHidden !== undefined) {
				filterHidden = this.params.filterHidden;
			}
		}
		const requestMax = Number.isInteger(this._currentMaxEntries) ? this._currentMaxEntries : this.params.firstMaxEntries;

		this.grid.classList.toggle("filtered", filterIsDirty);

		const usersSet = await this.usersSrv.list(null, filterType, filterHidden, filterGroups, requestMax, filterTxtRegExp, null, null, null, filterRoles, filterDisabled);
		if (!usersSet.userList) throw Error("usersSrv can't browse users");

		if (this.params.normalizeList) usersSet.userList = this.params.normalizeList(usersSet.userList, filterTxtRegExp);
		if (this.params.openFilter) usersSet.userList = usersSet.userList.filter(this.params.openFilter);
		if (this._contextualUserFilter) usersSet.userList = usersSet.userList.filter(this._contextualUserFilter);

		this.dataHolder.setDatas(usersSet.userList);

		// Refresh des btns
		if (this.razButton) this.razButton.disabled = !filterIsDirty;
		if (this.searchMoreBtn) DOM.setHidden(this.searchMoreBtn, requestMax >= this.params.fullMaxEntries || !usersSet.more);

		const nbMax = usersSet.userList.length;
		let msg = "";
		if (usersSet.more && this.searchMoreBtn && !this.searchMoreBtn.hidden) msg = `Seules les ${nbMax} premières entrées sont affichées.`
		else if (usersSet.more) msg = `Seules les ${nbMax} premières entrées sont affichées. Veuillez préciser la recherche...`
		this.msgElt.setCustomMsg(msg);

		if (selectedUsers && selectedUsers.length > 0) this.selectByJUser(selectedUsers, this.params.preserveSelectionOnFilter);
		if (this.params.forceSelectEntry && this.getSelectedUsers().length == 0) this.grid.setSelectedRows(0);
	}

	private _currentMaxEntries: number;

	private onKeyPress(this: HTMLInputElement, ev: KeyboardEvent) {
		if (ev.key === 'ArrowDown') {
			const tree = DOMSH.findHost(this) as UsersGrid<E>;
			if (tree.grid.getSelectedRow() !== undefined) tree.grid.focus();
		}
	}

}

REG.reg.registerSkin('server-users-grid', 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
	}

  :focus-visible {
	  outline: var(--focus-outline);
  }

  #head {
	  border-bottom: 1px solid var(--border-color);
  }

  #filter {
	  background: .1em / 1em no-repeat url(/@skin@/commons/icons/filter.svg);
	  padding-block: 2px;
	  padding-inline: 1.2em 2px;
	  display: flex;
	  min-width: 0;
	  min-height: 0;
	  align-items: center;
	  overflow: hidden;
	  flex-wrap: wrap;
  }

  #btnRazFilter {
	  margin-inline: 0;
	  padding-inline: 0;
  }

  #filter > * {
	  margin-block: 0;
	  margin-inline: .3rem 0;
  }

  #filterTxt {
	  min-width: 10em;
	  flex: 1;
  }

  #filterType {
	  border: none;
	  background-image: url("/@skin@/core/objects/userGroup.svg");
	  background-color: var(--form-bgcolor);
		background-repeat: no-repeat;
		background-size: auto 11px;
		background-position-y: center;
		background-position-x: 3px;
		min-width: 37px;
		max-width: 37px;
		padding-inline-start: 37px;
	  font-size: inherit;
  }

  #filterType[data-value=group] {
	  background-image: url("/@skin@/core/objects/group.svg");
  }

  #filterType[data-value=user] {
	  background-image: url("/@skin@/core/objects/user.svg");
  }


  #filterDisabled {
	  background-image: url("/@skin@/core/objects/user/disabledUnk.svg");
	  background-color: var(--form-bgcolor);
	  background-repeat: no-repeat;
	  background-size: auto calc(var(--icon-size) * .8);
	  background-position-y: center;
	  background-position-x: 3px;
	  min-width: 3rem;
	  max-width: var(--icon-size);
	  padding-inline-start: var(--icon-size);
	  font-size: inherit;
  }

  #filterDisabled[data-value=disabledPermanent] {
	  background-image: url("/@skin@/core/objects/user/disabled.svg");
  }

  #filterDisabled[data-value=NotDisabledPermanent] {
	  background-image: url("/@skin@/core/objects/user/enabled.svg");
  }


  #filterRoles {
	  min-width: 0;
  }

  #filterRoles[isNotSet] {
	  font-style: italic;
  }

  #filterRoles[isNotSet] > option {
		font-style: normal;
	}

	#filterRoles > option[value='main:~fallback'] {
		font-style: italic;
	}

	#filterGroup {
		min-width: 0;
	}

	c-input-users-panel {
		min-width: 12rem;
	}

	#msgBox {
		font-size: .8rem;
		display: flex;
		flex-direction: row;
	}

	#msg {
		color: var(--info-color);
		justify-content: flex-end;
		font-style: italic;
		margin-inline-end: .5rem;
	}

	#btnMore {
		margin: .1em;
		margin-inline-end: .5rem;
	}

	c-grid {
		flex: 1;
		border: none;
		background-color: var(--row-bgcolor);
	}
`);

customElements.define("server-users-grid", UsersGrid);

/** Colonne lorsque les datas est un JUser. */
export class CellBuilderAccountJUser extends CellBuilderString {

	constructor(protected reg: IReg<ICoreUniverseEnv>, public hideIcon: boolean = false, public showLongName = false) {
		super(null);
	}

	redrawCell(row: GridDataRowJsonTree<JUser>, root: HTMLElement): void {
		if (!root.firstElementChild)
			root.appendChild(<UserRef class="inline list" î={{reg: this.reg, user: row.rowDatas, withIcon: !this.hideIcon, showLongName: this.showLongName} as OUserRefInit}/>);
		else
			(root.firstElementChild as UserRef).user = row.rowDatas;
	}

	_getValue(row: GridDataRowJsonTree<JUser>) {
		return USER.getPrimaryName(row.rowDatas);
	}

}


/**
 * Colonne lorsque les datas ne contient que le account dans une key particulière
 * avec interrogation async du UsersSrv pour obtenir les infos.
 */
export class CellBuilderAccount extends CellBuilderString {

	cacheKey: symbol;

	constructor(protected reg: IReg<ICoreUniverseEnv>, dataKey: string, public hideIcon: boolean = false, protected knownUsers?: JUser[]) {
		super(dataKey);
		this.cacheKey = Symbol("CellBuilderAccount:" + dataKey);
	}

	async redrawCell(row: GridDataRowJsonTree<any>, root: HTMLElement) {
		let cache: JUser | Promise<JUser> = row.cacheHolder[this.cacheKey];
		const cacheHolder = row.cacheHolder;
		const rowDatas = row.rowDatas;

		if (cache === undefined) {
			let account: string;
			if (account = this.getAccount(row)) {
				this.redrawCellUser(row, root);
				cacheHolder[this.cacheKey] = cache = this.knownUsers?.find(entry => entry.account === account);
				if (!cacheHolder[this.cacheKey]) cacheHolder[this.cacheKey] = cache = this.reg.env.universe.useUsers.getUserBatch(account).then((user) => {
					if (cacheHolder[this.cacheKey] !== cache) return; //raceCond : les données de cette row ont évoluées.
					return cacheHolder[this.cacheKey] = user;
				});
			} else {
				//Pas de user
				cache = cacheHolder[this.cacheKey] = null;
			}
		}

		if (cache instanceof Promise) {
			//cache non reseté mais calcul en cours, on attend, on réaffiche le login en attendant.
			this.redrawCellUser(row, root);
			(root as any).findUserPending = cache; //raceCond pour refresh async de la line HTML.
			cache.then(() => {
				if ((root as any).findUserPending !== cache) return; //raceCond : cette ligne HTML a été redessinée
				(root as any).findUserPending = null; //cleanup
				// FIXME : trouver meilleur moyen pour retrouver la row courante plutot que de la reconstruire
				this.redrawCellUser({cacheHolder, rowDatas} as any, root);
			});
		} else {
			(root as any).findUserPending = null; //raceCond pour refresh async de la line HTML.
			this.redrawCellUser(row, root);
		}
	}

	private redrawCellUser(row: GridDataRowJsonTree<any>, root: HTMLElement) {
		const cache: JUser = row.cacheHolder[this.cacheKey];
		if (!root.firstElementChild)
			root.appendChild(<UserRef class="inline list" î={{reg: this.reg, withIcon: !this.hideIcon} as OUserRefInit}/>);
		if (!cache || cache instanceof Promise)
			(root.firstElementChild as UserRef).user = this.getAccount(row) ? {account: this.getAccount(row), isUnknown: true} : null;
		else
			(root.firstElementChild as UserRef).user = cache;
	}

	getAccount(row: GridDataRowJsonTree<any>): string {
		return row.rowDatas[this.dataKey];
	}

	/**
	 * Usage : sort
	 * ATTENTION : tri sur les **account**
	 * On ne peut récupérer la valeur affichée ici, car récupéré en async lorsque nécessaire sur le show de la cell
	 * Exploiter le cache quand dispo crée une **situation hétérogène**, ou des memes accounts (cache présent - cache absent) pourraient donc ne pas être adjacents
	 */
	_getValue(row: GridDataRowJsonTree<any>) {
		// const cache: JUser = row.cacheHolder[this.cacheKey];
		// return (cache && !(cache instanceof Promise)) ? USER.getPrimaryName(cache) : this.getAccount(row) || "";
		return this.getAccount(row) || "";
	}
}

/**
 * Colonne affichant une liste d'accounts, fournis via key particulière
 * avec interrogation async du UsersSrv pour obtenir les infos.
 */
export class CellBuilderAccounts extends CellBuilderString {

	cacheKey: symbol;

	constructor(protected reg: IReg<ICoreUniverseEnv>, dataKey: string, public hideIcon: boolean = false, public canSort: boolean = true) {
		super(dataKey);
		this.cacheKey = Symbol("CellBuilderAccount:" + dataKey);
	}

	async redrawCell(row: GridDataRowJsonTree<any>, root: HTMLElement) {
		let cache: JUser[] | Promise<JUser>[] = row.cacheHolder[this.cacheKey];
		const cacheHolder = row.cacheHolder;
		const rowDatas = row.rowDatas;

		if (cache === undefined) {
			let account: string;
			if (account = row.rowDatas[this.dataKey]) {
				this.redrawCellAccounts(row, root);//affiche les accounts en attendant
				cacheHolder[this.cacheKey] = cache = [];
				this.getAccounts(row)?.forEach((account) => {
					(cache as Promise<JUser>[]).push(this.reg.env.universe.useUsers.getUserBatch(account));
				})
				Promise.all(cache).then((users) => {
					if (cacheHolder[this.cacheKey] !== cache) return; //raceCond : les données de cette row ont évoluées.
					return cacheHolder[this.cacheKey] = users;
				});
			} else {
				//Pas de user
				cache = cacheHolder[this.cacheKey] = null;
			}
		}

		if (cache?.findIndex((entry: JUser | Promise<JUser>) => entry instanceof Promise) > -1) {
			//cache non reseté mais calcul en cours, on attend, on réaffiche le nombre en attendant.
			this.redrawCellAccounts(row, root);//affiche les accounts en attendant
			(root as any).findUsersPending = cache; //raceCond pour refresh async de la line HTML.
			Promise.all(cache).then(() => {
				if ((root as any).findUsersPending !== cache) return; //raceCond : cette line HTML a été redessinée
				(root as any).findUsersPending = null; //cleanup
				//(cacheHolder[this.cacheKey] as JUser[]) = (cacheHolder[this.cacheKey] as JUser[]).filter((user)=>user!=null);
				if (this.canSort) (cacheHolder[this.cacheKey] as JUser[]).sort((u1, u2) => USER.getPrimaryName(u1).localeCompare(USER.getPrimaryName(u2)));
				this.redrawCellUsers(cacheHolder[this.cacheKey], root);
			});
		} else {
			(root as any).findUserPending = null; //raceCond pour refresh async de la line HTML.
			this.redrawCellUsers(cacheHolder[this.cacheKey], root);
		}
	}

	private redrawCellUsers(users: JUser[], root: HTMLElement) {
		let valuesTxt: string[] = [];
		root.innerHTML = "";
		users?.forEach((user) => {
			root.appendChild(<UserRef class="inline list" î={{reg: this.reg, user: user, withTitle: false, withIcon: !this.hideIcon} as OUserRefInit}/>);
			if (user) valuesTxt.push(USER.getPrimaryName(user));
		});
		DOM.setAttr(root, "title", valuesTxt.length > 0 ? valuesTxt.join(', ') : null);
	}

	private redrawCellAccounts(row: GridDataRowJsonTree<any>, root: HTMLElement) {
		const values = this.getAccounts(row)?.join(', ');
		DOM.setTextContent(root, values || '');
		DOM.setAttr(root, "title", values);
	}

	getAccounts(row: GridDataRowJsonTree<any>): string[] {
		return row.rowDatas[this.dataKey];
	}

	/** Le tri d'une cellule contenant n valeurs non ordonnées n'a pas de sens */
	getColSortFn(): IRowSortFn | null {
		return undefined;
	}

}

